import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { useDebounce } from "hooks/useDebounce";
import { NONE_OPTION } from "components/NewAdvancedSearch/filters/noneOption";

const EMPTY_OPTIONS = [];

export function useSelectFilterOptions({
  data,
  queryFn,
  queryKey,
  props = {},
}) {
  const isAsync = typeof queryFn === "function";

  const { includeNoneOption = false, ...queryProps } = props;

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  const { data: asyncOptions = EMPTY_OPTIONS, isFetching } = useQuery({
    // queryProps scopes the fetch (e.g. campaign_ids), so it has to be in the
    // key: without it two campaigns' filter dropdowns share one cache entry.
    queryKey: ["select-filter-options", queryKey, debouncedSearch, queryProps],
    queryFn: () => queryFn({ search_query: debouncedSearch, ...queryProps }),
    enabled: isAsync,
    placeholderData: keepPreviousData,
  });

  const onInputValueChange = (next, { reason }) => {
    if (reason === "input-change") {
      setSearch(next);
    } else if (reason === "input-clear") {
      setSearch("");
    }
  };

  const showNoneOption =
    includeNoneOption &&
    (!isAsync ||
      NONE_OPTION.name.toLowerCase().includes(search.trim().toLowerCase()));

  const options = useMemo(() => {
    const sourced = isAsync ? asyncOptions : (data ?? EMPTY_OPTIONS);
    return showNoneOption ? [NONE_OPTION, ...sourced] : sourced;
  }, [isAsync, asyncOptions, data, showNoneOption]);

  return {
    isAsync,
    options,
    isFetching: isAsync && isFetching,
    onInputValueChange,
  };
}
